import PropTypes from 'prop-types';
import { useCallback, useState } from 'react';

import { useLiveFeedContext } from 'hb-react/frontoffice/apps/LiveFeed/contexts/LiveFeedContext';
import { useTopPostsContext } from 'hb-react/frontoffice/apps/LiveFeed/contexts/TopPostsContext';
import { getTopPostsPolicies } from 'hb-react/frontoffice/apps/LiveFeed/utils/actionItems';
import { getLinkUrlToPostItem } from 'hb-react/frontoffice/apps/LiveFeed/utils/getLinkUrlToPostItem';
import { toggleTopPosts } from 'hb-react/frontoffice/apps/LiveFeed/utils/toggleTopPosts';
import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import ConfirmationModal from 'hb-react/shared/components/DesignSystem/Modal/ConfirmationModal';
import useFeatureFlag from 'hb-react/shared/hooks/useFeatureFlag';
import { amplitudeLogger } from 'hb-react/shared/utils/amplitude';

import PostCard from './components/PostCard';

const PostCardContainer = ({
  total_comments: totalComments,
  content,
  id,
  index,
  likes,
  onTabKeyDown,
  type,
  user: { photo_thumb_url: avatarUrl, new_photo: newPhoto, id: userId },
}) => {
  const { onTriggerTopPost } = useTopPostsContext();
  const [isDeletionModalOpened, setIsDeletionModalOpened] = useState(false);

  const { flagCurrentSessionContext, flagEvUserPicturesToGlobalStorage } =
    useFeatureFlag();
  const { currentSession } = useDefaultAppContexts();
  const href = getLinkUrlToPostItem(type, id);

  const canEditTopPost = getTopPostsPolicies({
    type,
    isAdmin: flagCurrentSessionContext
      ? currentSession.checkAdminDualAccount && currentSession.networkAdmin
      : null,
  });

  const openDeletionModal = useCallback(
    () => setIsDeletionModalOpened(true),
    [],
  );
  const closeDeletionModal = useCallback(
    () => setIsDeletionModalOpened(false),
    [],
  );
  const removeTopPost = useCallback(async () => {
    const newTopPost = await toggleTopPosts({ type, itemId: id, userId });

    const newItem = {
      id,
      ...newTopPost,
    };

    onTriggerTopPost({ newItem, topPostHistory: true });
    closeDeletionModal();
  }, [type, id, userId, closeDeletionModal, onTriggerTopPost]);

  const { amplitudeConfig } = useLiveFeedContext();

  const amplitude = amplitudeLogger(amplitudeConfig);

  const onClick = useCallback(() => {
    amplitude.track('LiveFeed|TopPostClicked', {
      topPostPosition: index,
    });
  }, [amplitude, index]);

  // used to detect when the tab key is pressed over a card
  const onKeyDown = useCallback(
    ({ key }) => key === 'Tab' && onTabKeyDown?.(index),
    [index, onTabKeyDown],
  );

  return (
    <>
      <PostCard
        userId={userId}
        id={id}
        onClick={onClick}
        onKeyDown={onKeyDown}
        canEdit={canEditTopPost}
        href={href}
        totalComments={totalComments}
        content={content}
        likes={likes}
        type={type}
        avatarUrl={
          flagEvUserPicturesToGlobalStorage
            ? newPhoto?.thumbnail_url
            : avatarUrl
        }
        openModal={openDeletionModal}
      />
      {isDeletionModalOpened && (
        <ConfirmationModal
          onCloseModal={closeDeletionModal}
          confirmButtonLabel={__('Remove')}
          onConfirmation={removeTopPost}
          text={s__(
            'TopPosts|Do you want to remove this post from the Top Posts? Only admins are able to perform this action.',
          )}
          title={s__('TopPosts|Remove Top Post')}
        />
      )}
    </>
  );
};

PostCardContainer.propTypes = {
  total_comments: PropTypes.number.isRequired,
  content: PropTypes.string.isRequired,
  id: PropTypes.number.isRequired,
  index: PropTypes.number.isRequired,
  likes: PropTypes.object.isRequired,
  onTabKeyDown: PropTypes.func,
  type: PropTypes.string.isRequired,
  user: PropTypes.shape({
    photo_thumb_url: PropTypes.string,
    id: PropTypes.number,
  }).isRequired,
};

export default PostCardContainer;
